// Define strong types for models using a namespaced object

// Conservative context window limit that works across all models
export const MODEL_CONTEXT_WINDOW = 200_000;

// ============================================================================
// TYPES
// ============================================================================

export const PROVIDERS = {
  ANTHROPIC: 'anthropic',
  // Compatibility alias retained for direct callers; the active Azure chain starts at tertiary.
  AZURE: 'azure',
  // Compatibility alias retained for direct callers; this deployment is currently disabled.
  AZURE_SECONDARY: 'azure-secondary',
  AZURE_TERTIARY: 'azure-tertiary',
  AZURE_QUATERNARY: 'azure-quaternary',
  // Compatibility alias retained for historical telemetry and persisted metadata.
  BEDROCK_EU: 'bedrock-eu',
  BEDROCK_US: 'bedrock-us',
  // OpenAI-compatible Bedrock Mantle Responses endpoint, billed directly to AWS.
  BEDROCK_OPENAI_US: 'bedrock-openai-us',
  GEMINI: 'gemini',
  OPENAI: 'openai',
  // OpenAI API billed to cubic's own OpenAI organization. GPT-6 Luna only.
  OPENAI_DIRECT: 'openai-direct',
  VERTEX: 'vertex',
  DIGITALOCEAN_INFERENCE: 'digitalocean-inference',
  // OpenRouter routes, each pinned to one vetted upstream with broker failover
  // disabled (see `openrouter-routes.ts`). Flash-only for now.
  // Retired IDs remain for historical telemetry and persisted metadata only.
  OPENROUTER_DEEPINFRA: 'openrouter-deepinfra',
  OPENROUTER_PARASAIL: 'openrouter-parasail',
  OPENROUTER_TOGETHER: 'openrouter-together',
  // Text and tool calls only: Baseten's OpenRouter endpoint rejects `response_format`.
  OPENROUTER_BASETEN: 'openrouter-baseten',
  // Direct OpenAI-compatible Doubleword Inference API. Flash-only for now.
  DOUBLEWORD: 'doubleword',
  // Evaluation-only routes; Jev is deliberately outside the language-model catalog.
  TYPESAFE_DIRECT: 'typesafe-direct',
  VERCEL_GATEWAY_TYPESAFE: 'vercel-gateway-typesafe',
  FIREWORKS: 'fireworks',
  VERCEL_GATEWAY_OPENAI: 'vercel-gateway-openai',
  VERCEL_GATEWAY_AZURE: 'vercel-gateway-azure',
  // Retired; blocked permanently in `temporarily-disabled-providers.ts`.
  VERCEL_GATEWAY_DEEPINFRA: 'vercel-gateway-deepinfra',
  VERCEL_GATEWAY_BASETEN: 'vercel-gateway-baseten',
  VERCEL_GATEWAY_FIREWORKS: 'vercel-gateway-fireworks',
  // Preserve the official "Together AI" word boundary; the Gateway slug is `togetherai`.
  VERCEL_GATEWAY_TOGETHER_AI: 'vercel-gateway-togetherai',
  VERCEL_GATEWAY_BEDROCK: 'vercel-gateway-bedrock',
} as const;

export type ProviderType = (typeof PROVIDERS)[keyof typeof PROVIDERS];

/**
 * Human-readable provider labels used when building ModelConfig.logName values
 * for fallback chains. Keep these labels operationally recognizable, not API
 * identifiers: they appear in logs, analytics, and fallback error summaries.
 */
export const PROVIDER_LABELS = {
  [PROVIDERS.ANTHROPIC]: 'Anthropic',
  [PROVIDERS.AZURE]: 'Azure',
  [PROVIDERS.AZURE_SECONDARY]: 'Azure Secondary',
  [PROVIDERS.AZURE_TERTIARY]: 'Azure Tertiary',
  [PROVIDERS.AZURE_QUATERNARY]: 'Azure Quaternary',
  [PROVIDERS.BEDROCK_EU]: 'Bedrock EU',
  [PROVIDERS.BEDROCK_US]: 'Bedrock US',
  [PROVIDERS.BEDROCK_OPENAI_US]: 'Bedrock OpenAI US',
  [PROVIDERS.GEMINI]: 'Gemini',
  [PROVIDERS.OPENAI]: 'OpenAI',
  [PROVIDERS.OPENAI_DIRECT]: 'OpenAI Direct',
  [PROVIDERS.VERTEX]: 'Vertex',
  [PROVIDERS.DIGITALOCEAN_INFERENCE]: 'DigitalOcean Inference',
  [PROVIDERS.OPENROUTER_DEEPINFRA]: 'OpenRouter DeepInfra',
  [PROVIDERS.OPENROUTER_PARASAIL]: 'OpenRouter Parasail',
  [PROVIDERS.OPENROUTER_TOGETHER]: 'OpenRouter Together',
  // The label carries the capability so log names and dashboards say it too.
  [PROVIDERS.OPENROUTER_BASETEN]: 'OpenRouter Baseten (text only)',
  [PROVIDERS.DOUBLEWORD]: 'Doubleword',
  [PROVIDERS.TYPESAFE_DIRECT]: 'TypeSafe Direct',
  [PROVIDERS.VERCEL_GATEWAY_TYPESAFE]: 'Vercel Gateway TypeSafe',
  [PROVIDERS.FIREWORKS]: 'Fireworks',
  [PROVIDERS.VERCEL_GATEWAY_OPENAI]: 'Vercel Gateway OpenAI',
  [PROVIDERS.VERCEL_GATEWAY_AZURE]: 'Vercel Gateway Azure',
  [PROVIDERS.VERCEL_GATEWAY_DEEPINFRA]: 'Vercel Gateway DeepInfra',
  [PROVIDERS.VERCEL_GATEWAY_BASETEN]: 'Vercel Gateway Baseten',
  [PROVIDERS.VERCEL_GATEWAY_FIREWORKS]: 'Vercel Gateway Fireworks',
  [PROVIDERS.VERCEL_GATEWAY_TOGETHER_AI]: 'Vercel Gateway Together AI',
  [PROVIDERS.VERCEL_GATEWAY_BEDROCK]: 'Vercel Gateway Bedrock',
} satisfies Record<ProviderType, string>;

const FAMILY_KEY_MAP = {
  openai: 'OPENAI',
  anthropic: 'ANTHROPIC',
  xai: 'XAI',
  deepseek: 'DEEPSEEK',
  nvidia: 'NVIDIA',
  zai: 'ZAI',
  xiaomi: 'XIAOMI',
  moonshot: 'MOONSHOT',
  google_vertex: 'GOOGLE_VERTEX',
} as const;

export type ModelFamily = keyof typeof FAMILY_KEY_MAP;
type FamilyKey = (typeof FAMILY_KEY_MAP)[ModelFamily];

/**
 * Google thinking config mode for Gemini models.
 * - thinking_budget: Uses fixed token budget (Gemini 2.5 family)
 * - thinking_level_all: Supports minimal/low/medium/high levels (Gemini 3.1 Flash-Lite)
 * - thinking_level_low_medium_high: Supports low/medium/high, rejects minimal (Gemini 3.7 Flash)
 */
export type GoogleThinkingConfigType =
  | 'thinking_budget'
  | 'thinking_level_all'
  | 'thinking_level_low_medium_high';

/**
 * How a model can be asked for faster inference.
 *
 * - `openai-service-tier`: OpenAI `service_tier: 'priority'` (`'fast'` is a
 *   synonym since 2026-07-30; the same tier as Codex fast mode). Sent as
 *   `providerOptions.openai.serviceTier` and, on Gateway routes, also as
 *   `providerOptions.gateway.serviceTier`.
 * - `anthropic-speed`: Anthropic `speed: 'fast'` (Opus 5 / 4.8, first-party API
 *   only; Anthropic Priority Tier is not a speed feature). No catalog model
 *   uses it today; it exists so a future Claude rung is handled deliberately.
 * - `doubleword-fast-deployment`: Doubleword serves the same checkpoint from a
 *   separate, faster deployment under its own model id (the standard id with a
 *   `-fast` suffix, ~2x price). There is no request parameter: the attempt is
 *   sent to the fast deployment id instead (`getDoublewordChat`), and the
 *   served tier is read from the model id the response echoes.
 * - `none`: the model has no fast tier, or none has been verified on any route.
 */
export type FastTierMechanism =
  | 'openai-service-tier'
  | 'anthropic-speed'
  | 'doubleword-fast-deployment'
  | 'none';

/**
 * Declares how a catalog model can be made fast and which routes honour it, so
 * fast mode survives model rotation: a new model must decide this or CI fails.
 */
export type FastTierDeclaration = {
  readonly mechanism: FastTierMechanism;
  /**
   * Routes verified to serve the mechanism and to report what they served:
   * for `openai-service-tier`, routes that forward the tier request and echo
   * `providerMetadata.openai.serviceTier`; for `doubleword-fast-deployment`,
   * the route that hosts the fast deployment id and echoes the served model id
   * in its response. A fast-tier config on any other route runs at standard
   * speed. Must be a subset of `providerChain`; `models.test.ts` enforces both
   * invariants.
   */
  readonly providers: readonly ProviderType[];
};

/** One provider for the catalog entry's model. Cross-model choices belong to callers. */
type ProviderChainStep = {
  readonly provider: ProviderType;
};

/** Catalog entry for a single model - the source of truth */
type ModelCatalogEntry = {
  readonly family: ModelFamily;
  readonly key: string;
  readonly supportsReasoning: boolean;
  /** Defaults to true for reasoning-capable models when omitted. */
  readonly usesChatReasoningByDefault?: boolean;
  /** Defaults to false when omitted */
  readonly supportsXHigh?: boolean;
  /** Defaults to false when omitted. Distinct from `supportsXHigh`. */
  readonly supportsMax?: boolean;
  readonly supportsVerbosity: boolean;
  /** Present for Google models that expose provider-specific thinking controls */
  readonly googleThinkingConfigType?: GoogleThinkingConfigType;
  readonly providerChain: readonly ProviderChainStep[];
  /** Required: every model decides its fast-tier behaviour when it is added. */
  readonly fastTier: FastTierDeclaration;
};

// Base reasoning effort levels (all models)
export type ReasoningEffortBase = 'minimal' | 'low' | 'medium' | 'high';

// Extended reasoning effort levels with independent model capability gates.
export type ReasoningEffortWithXHigh = ReasoningEffortBase | 'xhigh';
export type ReasoningEffort = ReasoningEffortWithXHigh | 'max';

// Type for verbosity levels supported by OpenAI models
export type VerbosityLevel = 'low' | 'medium' | 'high';

// ============================================================================
// FALLBACK CHAIN HELPERS
// ============================================================================

/** Azure fallback chain while primary/secondary deployments are disabled. */
const AZURE_CHAIN: readonly ProviderChainStep[] = [
  { provider: 'azure-tertiary' },
  { provider: 'azure-quaternary' },
  // Temporarily disabled until these deployments recover:
  // { provider: 'azure-secondary' },
  // { provider: 'azure' },
] as const;

/**
 * Luna is available from Amazon Bedrock, OpenAI and Azure through Vercel AI
 * Gateway, and our direct Azure deployments. Bedrock is the catalog primary,
 * while tier rotation gives Bedrock 75% of healthy leads and Gateway OpenAI and
 * Gateway Azure 12.5% each. Direct Azure remains a same-model fallback.
 */
const GPT_5_6_LUNA_CHAIN: readonly ProviderChainStep[] = [
  { provider: PROVIDERS.BEDROCK_OPENAI_US },
  { provider: PROVIDERS.VERCEL_GATEWAY_OPENAI },
  { provider: PROVIDERS.VERCEL_GATEWAY_AZURE },
  ...AZURE_CHAIN,
] as const;

/**
 * GPT-6 Luna providers. Tier rotation splits healthy leads between Gateway OpenAI, Bedrock and
 * Gateway Azure; direct OpenAI leads none. Direct OpenAI has its own rate limits, separate from
 * the Gateway's per-team OpenAI limit, so it catches Gateway fallbacks before Bedrock.
 */
const GPT_6_LUNA_CHAIN: readonly ProviderChainStep[] = [
  { provider: PROVIDERS.VERCEL_GATEWAY_OPENAI },
  { provider: PROVIDERS.VERCEL_GATEWAY_AZURE },
  { provider: PROVIDERS.OPENAI_DIRECT },
  { provider: PROVIDERS.BEDROCK_OPENAI_US },
] as const;

/**
 * DeepSeek V4 Pro providers: native Azure deployments.
 * The Gateway Fireworks primary was removed 2026-08-25 with the rest of the
 * Fireworks integration (see DEEPSEEK_V4_FLASH_CHAIN for context); Azure
 * tertiary/quaternary are the model's remaining hosts and are currently
 * paused, so callers must choose a cross-model fallback. V4 Pro has no production
 * callers today.
 */
const DEEPSEEK_CHAIN: readonly ProviderChainStep[] = [
  { provider: PROVIDERS.AZURE_TERTIARY },
  { provider: PROVIDERS.AZURE_QUATERNARY },
] as const;

/**
 * V4 Flash has five registered routes after DeepInfra and Together were retired
 * on 2026-10-06: Doubleword 100, DigitalOcean 6, the two pinned OpenRouter
 * routes 100 each, and Gateway Baseten 3. The weighted
 * `deepseek-v4-flash-provider-split` in tier-rotation scales these weights by
 * outcome health. Provider-policy and credential filtering run before the draw.
 * Non-leaders stay as same-model failovers. The catalog order below is also
 * the CLI Chat Completions order, so Doubleword is first. Review callers may
 * explicitly append cross-model fallbacks after this chain.
 *
 * DigitalOcean's hang history makes it a risky host on paper, and the
 * $10k/month credit cap keeps its share at 6 rather than a larger lead.
 * Preemptive-skip and the health-aware draw suppress it by health rather
 * than position, so a sick DigitalOcean sheds to Doubleword or Baseten
 * instead of stalling on the timeout wall.
 *
 * The Fireworks tail was removed 2026-08-25 with the rest of the Fireworks
 * integration (business decision; the route was schema-enforcing and healthy
 * at removal). CONSEQUENCE: the contract-restricted accounts hard-coded in
 * CONTRACT_RESTRICTED_BILLING_ACCOUNT_IDS run DigitalOcean alone within this
 * tier and rely on the caller-appended cross-model rungs (typically Luna)
 * when DigitalOcean fails or is suppressed. (The broader enterprise
 * heuristic that also pinned accounts to DigitalOcean was removed
 * 2026-08-25.)
 *
 * The native Azure rungs were removed 2026-08-19: measured over 24h they stall
 * (>25s to first byte) on 20.1% (azure-tertiary) and 22.9% (azure-quaternary)
 * of calls with a p50 first byte of ~8s — rescue rungs that failed worse than
 * the primary they rescued.
 *
 * The Gateway routes are separate app-level attempts. Each request is pinned to
 * one provider, so a failure returns to our fallback engine instead of allowing
 * Gateway to choose another provider inside the same attempt.
 *
 * The Gateway Baseten rung was removed 2026-08-25 (hourly error rate flapping
 * between 0% and 95% — Gateway TTFB "deployment did not respond" plus
 * "Service temporarily unavailable", upstream capacity rather than schema
 * issues — failing hardest exactly when DeepInfra/DigitalOcean overflow
 * reached it, so the rung mostly added a dead hop to already-slow chains) and
 * restored 2026-08-27 as a cautious canary. It now uses the same outcome
 * health and recovery calculation as every route. Judge the canary on
 * provider success rates, error categories and TTFB before promoting its weight.
 *
 * Callers explicitly include Luna after Flash. An all-provider Flash outage
 * (seen briefly 2026-08-27 when a Gateway rate limit overlapped a DigitalOcean
 * circuit) can then fall through to that caller-selected model.
 *
 * OpenRouter was added 2026-09-03 as one cubic fallback rung backed by a
 * request-level pool of twelve vetted hosts with OpenRouter-side failover. That
 * shape failed in production (2026-09-03: 11.7% errors, latency p90 214s, no
 * per-host attribution because OpenRouter retried slow and rate-limited hosts
 * serially inside one cubic attempt) and was replaced on 2026-09-12 by five
 * separate rungs, each pinned to one upstream with `allow_fallbacks: false`
 * (see `openrouter-routes.ts`): Wafer, DeepInfra, Parasail, Together, and a
 * text-only Baseten. Wafer was removed 2026-09-23 after its `wafer/fast`
 * endpoint began leaking garbled reasoning into answers. DeepInfra and Together
 * were retired on 2026-10-06 because their contractual coverage through the
 * brokers remained unconfirmed. Each remaining route is a distinct app-level provider, so health
 * evidence, TTFB budgets, and telemetry are per upstream,
 * and each has its own weight and enable/disable control. The former
 * dashboard traffic caps are removed; weights and health now determine
 * initial selection among eligible routes. The text-only
 * Baseten rung is removed from structured-output requests before routing.
 *
 * Doubleword was added 2026-09-11 as a direct OpenAI-compatible Flash host
 * (`cubic-deepseek-ai/DeepSeek-V4-Flash-0731`) in the same weighted pool, first
 * as a capped canary (10% ceiling at launch, 30% later that day, shared 0–50%
 * ladder from 2026-09-12, 60% on 2026-09-17, code default 0%). On 2026-09-18
 * the admission cap was removed and the route became the uncapped Flash
 * primary — cheap and stable enough to take the largest lead share. Raised
 * to weight 100 on 2026-09-19 (DigitalOcean 6, Gateway DeepInfra 4) so the
 * mixed fleet-wide lead share was ~80% under the then-active OpenRouter caps.
 * DigitalOcean stays a smaller funded share so we burn credits (capped at
 * $10k/month) and is shed when unhealthy. Contract-sensitive accounts still
 * lose this route through provider-usage policy. It uses shared outcome health.
 *
 * Gateway Together AI was added 2026-08-28 after Vercel listed the
 * `togetherai` route for the pinned 0731 checkpoint. Local pinned-route probes
 * verified text generation, tool calling, and ZDR. Both adversarial and
 * cooperative nested-schema probes failed validation, so the route was
 * text-only. It was retired on 2026-10-06 alongside both DeepInfra routes
 * and OpenRouter Together; none can be restored by an administrative override.
 */
const DEEPSEEK_V4_FLASH_CHAIN: readonly ProviderChainStep[] = [
  { provider: PROVIDERS.DOUBLEWORD },
  { provider: PROVIDERS.DIGITALOCEAN_INFERENCE },
  // Pinned OpenRouter upstreams, fastest decoder first. Each has an independent
  // administrative enable/disable setting and outcome-health evidence.
  {
    provider: PROVIDERS.OPENROUTER_PARASAIL,
  },
  {
    provider: PROVIDERS.OPENROUTER_BASETEN,
  },
  {
    provider: PROVIDERS.VERCEL_GATEWAY_BASETEN,
  },
] as const;

/**
 * Legacy GLM 5.1/5.2 are served only by DigitalOcean on this chain.
 * Callers choose any cross-model fallback. GLM 5.1 is separately
 * paused through `DIGITALOCEAN_GLOBALLY_BLOCKED_MODEL_NAMES` and GLM 5.2 has
 * no call sites, so neither model routes traffic today.
 */
const GLM_CHAIN: readonly ProviderChainStep[] = [
  { provider: PROVIDERS.DIGITALOCEAN_INFERENCE },
] as const;

/**
 * GLM 5.3 Flash uses separate pinned Gateway attempts so provider failures stay
 * visible to the application fallback engine. Fireworks leads the GLM canary
 * after its 2026-08-31 Gateway qualification and the 2026-09-01 production
 * reorder; Baseten is the final
 * same-model rescue rung. The experiment caller appends its unchanged
 * Luna-first baseline.
 */
const GLM_5P3_FLASH_CHAIN: readonly ProviderChainStep[] = [
  {
    provider: PROVIDERS.VERCEL_GATEWAY_FIREWORKS,
  },
  {
    provider: PROVIDERS.VERCEL_GATEWAY_BASETEN,
  },
] as const;

/**
 * Claude Sonnet 5 providers: Bedrock regions in preference order. Callers
 * explicitly choose Luna where that fallback sequence is needed. The 3.7/4.x
 * Sonnet and Opus catalog was retired 2026-08-28 (near-zero live traffic; every
 * review flow had already migrated to Sonnet 5, Gemini, or Luna); Opus 5.5
 * reaches Bedrock through Vercel AI Gateway instead.
 */
const ANTHROPIC_BEDROCK_EU_FIRST_CHAIN: readonly ProviderChainStep[] = [
  { provider: 'bedrock-eu' },
  { provider: 'bedrock-us' },
] as const;

/**
 * Fallback chain for OpenAI-family API models.
 *
 * Route through first-party OpenAI on Vercel AI Gateway first, then use the
 * Azure deployments as provider-level fallbacks. Every OpenAI API model in the
 * catalog must have a verified Gateway route in
 * `VERCEL_GATEWAY_OPENAI_MODEL_NAMES` before it may use this chain.
 */
const OPENAI_CHAIN: readonly ProviderChainStep[] = [
  { provider: PROVIDERS.VERCEL_GATEWAY_OPENAI },
  ...AZURE_CHAIN,
] as const;

// ============================================================================
// FAST TIER DECLARATIONS
// ============================================================================

/** The model has no fast tier, or none has been verified on any of its routes. */
const NO_FAST_TIER: FastTierDeclaration = { mechanism: 'none', providers: [] };

/**
 * OpenAI priority processing through the Gateway's first-party OpenAI route.
 *
 * Verified 2026-09-22 (MRG-1630) for the pre-GPT-6 models using this declaration,
 * with one call per model carrying `serviceTier: 'priority'`: each came back with
 * `providerMetadata.openai.serviceTier = 'priority'` (and
 * `providerMetadata.gateway.serviceTier = 'priority'`, billed at roughly twice
 * the standard price). GPT-6 Luna/Sol were verified separately on 2026-09-28:
 * `providerMetadata.openai.serviceTier = 'fast'` and
 * `providerMetadata.gateway.serviceTier = 'priority'`, at twice the standard cost.
 * The nano models were served `'default'` and keep
 * `NO_FAST_TIER`. Bedrock Mantle is not listed: it rejects `priority` for Luna
 * with HTTP 400 and `@ai-sdk/openai` strips the option for its `openai.`-prefixed
 * model id anyway. The Azure deployments are unverified. See
 * `src/lib/ai/README.md` ("Fast tier").
 */
const OPENAI_GATEWAY_FAST_TIER: FastTierDeclaration = {
  mechanism: 'openai-service-tier',
  providers: [PROVIDERS.VERCEL_GATEWAY_OPENAI],
};

/**
 * Doubleword's dedicated fast deployment of the pinned DeepSeek V4 Flash 0731
 * checkpoint (`PROVIDER_MODEL_IDS.DOUBLEWORD.DEEPSEEK_V4_FLASH_0731_FAST`), live
 * 2026-10-05 and billed at twice the standard deployment. Quoted at a 150-200
 * output tokens/s p50; the standard deployment measures 165 TPS p50 off-peak
 * but 85-95 TPS at the 13-17 UTC peak (PostHog `$ai_generation`, 2026-10-02 to
 * 2026-10-05), so the gain is expected at peak and is read from the fast-tier
 * arm, not assumed. Only the Doubleword route serves it; DigitalOcean, DeepInfra
 * and the OpenRouter upstreams keep standard speed.
 */
const DOUBLEWORD_FAST_DEPLOYMENT_TIER: FastTierDeclaration = {
  mechanism: 'doubleword-fast-deployment',
  providers: [PROVIDERS.DOUBLEWORD],
};

// ============================================================================
// MODEL CATALOG - Single Source of Truth
// ============================================================================

/**
 * The authoritative catalog of all models.
 *
 * To add a new model:
 * 1. Add an entry here with all properties
 * 2. That's it! MODELS, types, and runtime constants are derived automatically.
 *
 * Each entry includes:
 * - family: Provider family (openai/anthropic/xai/deepseek/google_vertex)
 * - key: The key used in MODELS object (e.g., 'GPT_41' for MODELS.OPENAI.GPT_41)
 * - supportsReasoning: Whether the model accepts reasoningEffort parameter
 * - supportsXHigh: Whether the model supports 'xhigh' reasoning (e.g. gpt-5.3-codex, gpt-5.4-mini, gpt-5.5)
 * - supportsMax: Whether the model supports the distinct literal 'max' reasoning effort
 * - supportsVerbosity: Whether the model supports verbosity control
 * - googleThinkingConfigType: Gemini thinking config mode (Google models only)
 * - providerChain: Ordered providers for this model only; callers choose cross-model fallbacks
 * - fastTier: How the model can be made fast and which routes honour it (see FastTierDeclaration)
 */
const MODEL_CATALOG = {
  // =========================================================================
  // OpenAI Models
  // =========================================================================
  'gpt-4.1': {
    family: 'openai',
    key: 'GPT_41',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-4.1-mini': {
    family: 'openai',
    key: 'GPT_41_MINI',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-4.1-nano': {
    family: 'openai',
    key: 'GPT_41_NANO',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-5.3-codex': {
    family: 'openai',
    key: 'GPT_5_3_CODEX',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-5.4': {
    family: 'openai',
    key: 'GPT_5_4',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-5.5': {
    family: 'openai',
    key: 'GPT_5_5',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: true,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-5.6-terra': {
    family: 'openai',
    key: 'GPT_5_6_TERRA',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  // Added for the auto-fix loop's lead coding agent (`coding-agent-service/harnesses/models.ts`);
  // it follows the Terra chain (Gateway OpenAI, then Azure) rather than Luna's
  // Bedrock-first chain because Sol has no Bedrock Mantle deployment yet.
  'gpt-5.6-sol': {
    family: 'openai',
    key: 'GPT_5_6_SOL',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  // Sol's successor; the auto-fix loop's sidekick coding agent. Same chain as Sol:
  // Gateway OpenAI first, no Bedrock deployment.
  'gpt-6-sol': {
    family: 'openai',
    key: 'GPT_6_SOL',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: false,
    // Probed 2026-09-28 through the Gateway: served `priority` (reported as `fast`), 2x cost.
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  // The coding agents' lead and sidekick. Same chain as GPT-6 Sol.
  'gpt-6.1-sol': {
    family: 'openai',
    key: 'GPT_6_1_SOL',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: false,
    // Not probed for `priority`.
    fastTier: NO_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-5.6-luna': {
    family: 'openai',
    key: 'GPT_5_6_LUNA',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsMax: true,
    supportsVerbosity: false,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: GPT_5_6_LUNA_CHAIN,
  },
  'gpt-6-luna': {
    family: 'openai',
    key: 'GPT_6_LUNA',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsMax: true,
    supportsVerbosity: false,
    // Probed 2026-09-28 through the Gateway: `priority` was served (`openai.serviceTier: 'fast'`,
    // `gateway.serviceTier: 'priority'`, `gateway.routing.speed: 'fast'`) at 2x the standard cost
    // of the same prompt; see README "Fast tier". Bedrock Mantle is not listed (rejects the tier).
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: GPT_6_LUNA_CHAIN,
  },
  'gpt-5.4-mini': {
    family: 'openai',
    key: 'GPT_5_4_MINI',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: true,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-5-mini': {
    family: 'openai',
    key: 'GPT_5_MINI',
    supportsReasoning: true,
    supportsVerbosity: true,
    fastTier: OPENAI_GATEWAY_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  // The nano models were served `default` when asked for `priority` through the
  // Gateway (2026-09-22 probe), so they stay on no fast tier.
  'gpt-5-nano': {
    family: 'openai',
    key: 'GPT_5_NANO',
    supportsReasoning: true,
    usesChatReasoningByDefault: false,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },
  'gpt-5.4-nano': {
    family: 'openai',
    key: 'GPT_5_4_NANO',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: OPENAI_CHAIN,
  },

  // =========================================================================
  // XAI Models
  // =========================================================================
  'grok-4-fast-non-reasoning': {
    family: 'xai',
    key: 'GROK_4_FAST_NON_REASONING',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: AZURE_CHAIN,
  },
  'grok-4': {
    family: 'xai',
    key: 'GROK_4',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: AZURE_CHAIN,
  },
  // NOT USE, MODEL IS DOWN ON AZURE
  'grok-4-fast-reasoning': {
    family: 'xai',
    key: 'GROK_4_FAST',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: AZURE_CHAIN,
  },

  // =========================================================================
  // Anthropic Models
  // =========================================================================
  'claude-sonnet-5': {
    family: 'anthropic',
    key: 'CLAUDE_SONNET_5',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    // Sonnet 5 is Bedrock-only in our provider set.
    providerChain: ANTHROPIC_BEDROCK_EU_FIRST_CHAIN,
  },
  // Launched on Bedrock 2026-10-07. The first Haiku with effort control (low through max, xhigh
  // included); it thinks adaptively by default. Bedrock bills it by prompt length: prompts over
  // 100K tokens pay 5x per token, so PostHog gets per-call prices (`claude-haiku-5-5-pricing.ts`).
  'claude-haiku-5-5': {
    family: 'anthropic',
    key: 'CLAUDE_HAIKU_5_5',
    supportsReasoning: true,
    supportsXHigh: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: ANTHROPIC_BEDROCK_EU_FIRST_CHAIN,
  },
  // Served only on Gateway's Bedrock route under zero data retention: the model
  // instance pins every request there. Callers that need another rung choose their
  // own fallback models. Opus 5.5 always thinks and rejects a forced tool choice, so
  // `isAnthropicModelWithThinking` counts it as thinking and tool loops ask for `auto`.
  // That route rejects its native JSON output too, so generateObject always fails on it:
  // use generateText.
  'claude-opus-5-5': {
    family: 'anthropic',
    key: 'CLAUDE_OPUS_5_5',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: [{ provider: PROVIDERS.VERCEL_GATEWAY_BEDROCK }],
  },

  // =========================================================================
  // DeepSeek Models
  // =========================================================================
  'DeepSeek-V4-Flash': {
    family: 'deepseek',
    key: 'DEEPSEEK_V4_FLASH',
    supportsReasoning: true,
    // DeepSeek V4 Flash supports "max" reasoning, which we represent internally as xhigh.
    supportsXHigh: true,
    supportsVerbosity: false,
    fastTier: DOUBLEWORD_FAST_DEPLOYMENT_TIER,
    providerChain: DEEPSEEK_V4_FLASH_CHAIN,
  },
  'DeepSeek-V4-Pro': {
    family: 'deepseek',
    key: 'DEEPSEEK_V4_PRO',
    supportsReasoning: true,
    // DeepSeek V4 Pro supports "max" reasoning, which we represent internally as xhigh.
    supportsXHigh: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: DEEPSEEK_CHAIN,
  },

  // =========================================================================
  // NVIDIA Models
  // =========================================================================
  'nvidia/nemotron-nano-9b-v2': {
    family: 'nvidia',
    key: 'NEMOTRON_NANO_9B_V2',
    supportsReasoning: false,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: [{ provider: PROVIDERS.VERCEL_GATEWAY_BEDROCK }],
  },

  // =========================================================================
  // Z.ai Models
  // =========================================================================
  'glm-5p1': {
    family: 'zai',
    key: 'GLM_5P1',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: GLM_CHAIN,
  },
  'glm-5p2': {
    family: 'zai',
    key: 'GLM_5P2',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    providerChain: GLM_CHAIN,
  },
  'glm-5p3-flash': {
    family: 'zai',
    key: 'GLM_5P3_FLASH',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    // Baseten and Fireworks passed the model-specific schema probes.
    // Callers retain their own baseline model chain as the cross-model fallback.
    providerChain: GLM_5P3_FLASH_CHAIN,
  },

  // =========================================================================
  // Moonshot Models
  // =========================================================================
  'Kimi-K2-6': {
    family: 'moonshot',
    key: 'KIMI_K2_6',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    // Kimi K2-6 uses Azure Chat Completions as the control path, with a
    // provider-level DigitalOcean canary applied in tier-rotation.
    providerChain: AZURE_CHAIN,
  },
  'Kimi-K2.5': {
    family: 'moonshot',
    key: 'KIMI_K2P5',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    // Kimi K2.5 doesn't support Azure Responses API, uses Chat Completions via Azure
    providerChain: AZURE_CHAIN,
  },

  // =========================================================================
  // Google Vertex Models
  // =========================================================================
  'gemini-2.5-pro': {
    family: 'google_vertex',
    key: 'GEMINI_2_5_PRO',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    googleThinkingConfigType: 'thinking_budget',
    providerChain: [{ provider: PROVIDERS.VERTEX }],
  },
  'gemini-3.7-flash': {
    family: 'google_vertex',
    key: 'GEMINI_3_7_FLASH',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    // Gemini 3.7 Flash accepts low/medium/high thinking levels but rejects
    // 'minimal' (GA 2026-08-13); the mapping clamps minimal up to low.
    googleThinkingConfigType: 'thinking_level_low_medium_high',
    providerChain: [{ provider: PROVIDERS.VERTEX }],
  },
  'gemini-3.1-flash-lite': {
    family: 'google_vertex',
    key: 'GEMINI_3_1_FLASH_LITE',
    supportsReasoning: true,
    supportsVerbosity: false,
    fastTier: NO_FAST_TIER,
    googleThinkingConfigType: 'thinking_level_all',
    providerChain: [{ provider: PROVIDERS.VERTEX }],
  },
} as const satisfies Record<string, ModelCatalogEntry>;

// Export the catalog for use in other files
export { MODEL_CATALOG };

// ============================================================================
// DERIVED TYPES
// ============================================================================

/** All model IDs as a union type */
export type ModelName = keyof typeof MODEL_CATALOG;

/** Extract models that support reasoning */
export type ReasoningModelName = {
  [K in ModelName]: (typeof MODEL_CATALOG)[K]['supportsReasoning'] extends true ? K : never;
}[ModelName];

/** Helper type to access catalog entries with optional fields */
type CatalogEntry<M extends ModelName> = (typeof MODEL_CATALOG)[M] & ModelCatalogEntry;

/** Extract models that support xhigh reasoning */
export type XHighModelName = {
  [K in ModelName]: CatalogEntry<K> extends { supportsXHigh: true } ? K : never;
}[ModelName];

/** Extract models that support literal max reasoning */
export type MaxReasoningModelName = {
  [K in ModelName]: CatalogEntry<K> extends { supportsMax: true } ? K : never;
}[ModelName];

/** Extract models that support verbosity */
export type OpenAIVerbosityModelName = {
  [K in ModelName]: (typeof MODEL_CATALOG)[K]['supportsVerbosity'] extends true ? K : never;
}[ModelName];

/**
 * Conditional reasoning effort type based on model capability.
 * - For max-capable models: allows every reasoning effort, including literal 'max'
 * - For xhigh-capable models: allows 'minimal' | 'low' | 'medium' | 'high' | 'xhigh'
 * - For other reasoning models: only allows 'minimal' | 'low' | 'medium' | 'high'
 *
 * Note: This only works at compile-time when the model is a literal type.
 * Runtime validation is still required via assertReasoningEffortAllowed().
 */
export type ReasoningEffortForModel<M extends ModelName> = M extends MaxReasoningModelName
  ? ReasoningEffort
  : M extends XHighModelName
    ? ReasoningEffortWithXHigh
    : M extends ReasoningModelName
      ? ReasoningEffortBase
      : never;

// ============================================================================
// DERIVED RUNTIME CONSTANTS
// ============================================================================

/** Typed Object.keys for MODEL_CATALOG */
function catalogKeys(): ModelName[] {
  return Object.keys(MODEL_CATALOG) as ModelName[];
}

/** Typed Object.entries for MODEL_CATALOG */
function catalogEntries(): [ModelName, ModelCatalogEntry][] {
  return Object.entries(MODEL_CATALOG) as [ModelName, ModelCatalogEntry][];
}

/** All model IDs as an array */
export const ALL_MODEL_VALUES = catalogKeys();

/** Models that support reasoning effort */
export const REASONING_MODELS = catalogEntries()
  .filter(([, entry]) => entry.supportsReasoning)
  .map(([id]) => id);

/** Models that support xhigh reasoning (gpt-5.3-codex, gpt-5.4-mini, gpt-5.5) */
export const XHIGH_MODELS = catalogEntries()
  .filter(([, entry]) => entry.supportsXHigh === true)
  .map(([id]) => id);

/** Models that support the distinct literal max reasoning effort */
export const MAX_REASONING_MODELS = catalogEntries()
  .filter(([, entry]) => entry.supportsMax === true)
  .map(([id]) => id);

/** Models that support verbosity control */
export const VERBOSITY_MODELS = catalogEntries()
  .filter(([, entry]) => entry.supportsVerbosity)
  .map(([id]) => id);

/**
 * Type guard to check if a string is a valid model name.
 * Use this for runtime validation of user-provided model names.
 */
export function isValidModelName(value: string): value is ModelName {
  return value in MODEL_CATALOG;
}

/**
 * Type guard to check if a model name supports reasoning.
 */
export function isReasoningModelName(model: ModelName): model is ReasoningModelName {
  return getCatalogEntry(model).supportsReasoning;
}

/**
 * Whether chat should enable a reasoning model's default provider options.
 * Models can support explicit reasoning in targeted workloads without making
 * it the default for interactive chat.
 */
export function usesChatReasoningByDefault(model: ModelName): boolean {
  const entry = getCatalogEntry(model);
  return entry.supportsReasoning && entry.usesChatReasoningByDefault !== false;
}

// ============================================================================
// MODELS OBJECT - Derived from Catalog
// ============================================================================

// Build MODELS object from catalog
function buildModelsObject() {
  const result: Record<FamilyKey, Record<string, string>> = {
    OPENAI: {},
    XAI: {},
    ANTHROPIC: {},
    DEEPSEEK: {},
    NVIDIA: {},
    ZAI: {},
    XIAOMI: {},
    MOONSHOT: {},
    GOOGLE_VERTEX: {},
  };

  for (const [modelId, entry] of Object.entries(MODEL_CATALOG)) {
    const familyKey = FAMILY_KEY_MAP[entry.family];
    result[familyKey][entry.key] = modelId;
  }

  return result;
}

// Type for the MODELS object that preserves literal types
type ModelsType = {
  OPENAI: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'openai'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  XAI: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'xai'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  ANTHROPIC: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'anthropic'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  DEEPSEEK: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'deepseek'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  NVIDIA: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'nvidia'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  ZAI: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'zai'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  XIAOMI: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'xiaomi'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  MOONSHOT: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'moonshot'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
  GOOGLE_VERTEX: {
    [
      K in keyof typeof MODEL_CATALOG as (typeof MODEL_CATALOG)[K]['family'] extends 'google_vertex'
        ? (typeof MODEL_CATALOG)[K]['key']
        : never
    ]: K;
  };
};

/** Helper to get a catalog entry with normalized type */
function getCatalogEntry(model: ModelName): ModelCatalogEntry {
  return MODEL_CATALOG[model];
}

/** Returns the developer family for a logical model name. */
export function getModelFamily(model: ModelName): ModelFamily {
  return getCatalogEntry(model).family;
}

/**
 * Logical model names used throughout the codebase.
 *
 * These are the identifiers we use in our code to refer to models.
 * Some match the provider's API exactly (e.g., 'gpt-4.1'), while others
 * are logical names that get translated to provider-specific IDs
 * (e.g., 'claude-sonnet-5' → 'global.anthropic.claude-sonnet-5' for Bedrock).
 *
 * This object is derived from MODEL_CATALOG - do not edit directly.
 */
export const MODELS = buildModelsObject() as ModelsType;

/**
 * Returns true only for models that use OpenAI/Azure Responses and therefore
 * support request-level `promptCacheKey`.
 */
export function supportsOpenAiPromptCacheKeyForModel(model: string): boolean {
  if (!isValidModelName(model)) {
    return false;
  }

  return getCatalogEntry(model).family === 'openai';
}

/**
 * Exact allowlists for OpenAI Responses `prompt_cache_retention: "24h"`.
 *
 * Extended retention support is provider/model-specific and can vary from
 * normal prompt caching support, so this intentionally does not infer support
 * from a model family or prefix. When updating the lists, use exact model IDs
 * from the docs and keep Azure/Gateway OpenAI differences explicit.
 *
 * @see https://developers.openai.com/api/docs/guides/prompt-caching
 * @see https://learn.microsoft.com/en-us/azure/foundry/openai/how-to/prompt-caching
 */
const AZURE_OPENAI_PROMPT_CACHE_RETENTION_MODELS: ReadonlySet<ModelName> = new Set([
  MODELS.OPENAI.GPT_41,
  MODELS.OPENAI.GPT_5_3_CODEX,
  MODELS.OPENAI.GPT_5_4,
]);

/**
 * OpenAI Native via Gateway differs from Azure: `gpt-5.5` is included, while
 * `gpt-5.3-codex` is not included here unless OpenAI documents it.
 */
const GATEWAY_OPENAI_PROMPT_CACHE_RETENTION_MODELS: ReadonlySet<ModelName> = new Set([
  MODELS.OPENAI.GPT_41,
  MODELS.OPENAI.GPT_5_4,
  MODELS.OPENAI.GPT_5_5,
]);

function isAzureOpenAiProvider(provider: ProviderType): boolean {
  return (
    provider === PROVIDERS.AZURE ||
    provider === PROVIDERS.AZURE_SECONDARY ||
    provider === PROVIDERS.AZURE_TERTIARY ||
    provider === PROVIDERS.AZURE_QUATERNARY ||
    provider === PROVIDERS.OPENAI
  );
}

function isGatewayOpenAiProvider(provider: ProviderType): boolean {
  return provider === PROVIDERS.VERCEL_GATEWAY_OPENAI;
}

/**
 * Returns true when OpenAI/Azure docs list this exact model/provider pair as
 * supporting extended prompt-cache retention.
 */
export function supportsOpenAiPromptCacheRetention(model: string, provider: ProviderType): boolean {
  if (!isValidModelName(model)) {
    return false;
  }

  if (isAzureOpenAiProvider(provider)) {
    return AZURE_OPENAI_PROMPT_CACHE_RETENTION_MODELS.has(model);
  }

  if (isGatewayOpenAiProvider(provider)) {
    return GATEWAY_OPENAI_PROMPT_CACHE_RETENTION_MODELS.has(model);
  }

  return false;
}

// ============================================================================
// PROVIDER MODEL IDS
// ============================================================================

/**
 * Provider-specific model IDs that differ from our logical model names.
 *
 * Why this exists:
 * - MODELS contains our internal logical names (e.g., 'claude-sonnet-5')
 * - Some providers require different IDs in their API calls
 * - For example, Bedrock requires 'global.anthropic.claude-sonnet-5'
 *   but we refer to the model as 'claude-sonnet-5' everywhere in our code
 *
 * getModelInstance() handles the translation from logical names to provider IDs.
 */
export const PROVIDER_MODEL_IDS = {
  BEDROCK_OPENAI: {
    GPT_5_6_LUNA: 'openai.gpt-5.6-luna',
    // GPT-6 Luna supports Responses on bedrock-runtime, not bedrock-mantle.
    GPT_6_LUNA: 'global.openai.gpt-6-luna',
  },
  BEDROCK: {
    // Global inference profile for cross-region routing
    CLAUDE_SONNET_5: 'global.anthropic.claude-sonnet-5',
    CLAUDE_HAIKU_5_5: 'global.anthropic.claude-haiku-5-5',
  },
  FIREWORKS: {
    // Kimi K2.5 uses Azure as primary, but Fireworks needs its platform-specific model path
    KIMI_K2P5: 'accounts/fireworks/models/kimi-k2p5',
    // DeepSeek V4 served via Fireworks' OpenAI-compatible Chat Completions endpoint.
    DEEPSEEK_V4_FLASH: 'accounts/fireworks/models/deepseek-v4-flash',
    DEEPSEEK_V4_PRO: 'accounts/fireworks/models/deepseek-v4-pro',
    GLM_5P1: 'accounts/fireworks/models/glm-5p1',
    GLM_5P2: 'accounts/fireworks/models/glm-5p2',
  },
  DIGITALOCEAN_INFERENCE: {
    KIMI_K2_6: 'kimi-k2.6',
    DEEPSEEK_V4_FLASH: 'deepseek-v4-flash-0731',
    DEEPSEEK_V4_PRO: 'deepseek-v4-pro',
    GLM_5P1: 'glm-5.1',
    GLM_5P2: 'glm-5.2',
  },
  OPENROUTER: {
    // Pin the checkpoint; the floating Flash alias may move independently.
    DEEPSEEK_V4_FLASH_0731: 'deepseek/deepseek-v4-flash-0731',
  },
  DOUBLEWORD: {
    // Pin Cubic's dedicated 0731 deployment, provisioned for our latency test.
    // The public deepseek-ai model ID routes to a different deployment.
    DEEPSEEK_V4_FLASH_0731: 'cubic-deepseek-ai/DeepSeek-V4-Flash-0731',
    // The same checkpoint on Doubleword's faster dedicated deployment (2x price);
    // sent only by a fast-tier attempt, see `DOUBLEWORD_FAST_DEPLOYMENT_TIER`.
    DEEPSEEK_V4_FLASH_0731_FAST: 'cubic-deepseek-ai/DeepSeek-V4-Flash-0731-fast',
  },
  VERCEL_GATEWAY: {
    // Vercel AI Gateway model IDs. Routed provider IDs live under their provider
    // buckets and are linked in provider-policies.ts.
    DEEPSEEK_V4_FLASH_0731: 'deepseek/deepseek-v4-flash-0731',
    // Retained for explicit callers and historical attribution. The production
    // Flash chain pins every provider attempt to the 0731 checkpoint.
    DEEPSEEK_V4_FLASH_PREVIEW: 'deepseek/deepseek-v4-flash',
    DEEPSEEK_V4_PRO: 'deepseek/deepseek-v4-pro',
    NEMOTRON_NANO_9B_V2: 'nvidia/nemotron-nano-9b-v2',
    CLAUDE_OPUS_5_5: 'anthropic/claude-opus-5.5',
    GLM_5P1: 'zai/glm-5.1',
    GLM_5P2: 'zai/glm-5.2',
    GLM_5P3_FLASH: 'zai/glm-5.3-flash',
  },
} as const;

// ============================================================================
// MODEL CONFIG TYPE
// ============================================================================

/**
 * Per-attempt fast-tier request. Honoured only when the model's catalog
 * `fastTier` declares a mechanism and lists the attempt's provider; on any
 * other route the attempt runs at standard speed and telemetry reports
 * `requestedServiceTier: null` next to `fastTierRequested: true`.
 */
type ModelFastTierConfig = {
  fastTier?: boolean;
};

// Redefine ModelConfig so reasoningEffort is only allowed for reasoning models
export type ModelConfig = (
  | {
      provider: ProviderType;
      name: ReasoningModelName;
      logName: string;
      reasoningEffort?: ReasoningEffort;
      verbosity?: VerbosityLevel;
    }
  | {
      provider: ProviderType;
      // Other models must not include reasoningEffort
      name: Exclude<ModelName, ReasoningModelName>;
      logName: string;
      reasoningEffort?: never;
      verbosity?: VerbosityLevel;
    }
) &
  ModelFastTierConfig;

// ============================================================================
// RUNTIME VALIDATION
// ============================================================================

/**
 * Validates that the reasoning effort is allowed for the given model.
 * Throws an error if:
 * - effort is 'max' but model doesn't support it
 * - effort is 'xhigh' but model doesn't support it
 * - effort is provided but model doesn't support reasoning
 */
export function assertReasoningEffortAllowed(model: ModelName, effort?: ReasoningEffort): void {
  if (!effort) {
    return;
  }

  const entry = getCatalogEntry(model);

  if (!entry.supportsReasoning) {
    throw new Error(
      `Model "${model}" does not support reasoning effort. ` +
        `Reasoning-capable models: ${REASONING_MODELS.join(', ')}`,
    );
  }

  if (effort === 'max' && entry.supportsMax !== true) {
    throw new Error(
      `Model "${model}" does not support 'max' reasoning effort. ` +
        `Only ${MAX_REASONING_MODELS.join(', ')} support 'max'.`,
    );
  }

  if (isDeepSeekV4Model(model) && !isNativeDeepSeekV4Effort(model, effort)) {
    throw new Error(
      `Model "${model}" only supports reasoning effort 'high' or 'xhigh' ` +
        `(DeepSeek's provider-native maximum). ` +
        `Use undefined for non-reasoning mode.`,
    );
  }

  if (effort === 'xhigh' && entry.supportsXHigh !== true) {
    throw new Error(
      `Model "${model}" does not support 'xhigh' reasoning effort. ` +
        `Only ${XHIGH_MODELS.join(', ')} support 'xhigh'.`,
    );
  }
}

/**
 * Checks if a model supports verbosity control.
 */
export function supportsVerbosity(model: ModelName): boolean {
  return MODEL_CATALOG[model].supportsVerbosity;
}

/**
 * Checks if a model supports reasoning effort.
 */
export function supportsReasoning(model: ModelName): boolean {
  return MODEL_CATALOG[model].supportsReasoning;
}

/**
 * Checks if a model supports xhigh reasoning effort.
 */
export function supportsXHigh(model: ModelName): boolean {
  return getCatalogEntry(model).supportsXHigh === true;
}

/** Returns how the model can be made fast and which routes honour it. */
export function getFastTierDeclaration(model: ModelName): FastTierDeclaration {
  return getCatalogEntry(model).fastTier;
}

export type DoublewordProviderModelId =
  (typeof PROVIDER_MODEL_IDS.DOUBLEWORD)[keyof typeof PROVIDER_MODEL_IDS.DOUBLEWORD];

/**
 * Doubleword fast deployments by the standard deployment id they mirror: the
 * one place the pairing is declared. A fast-tier attempt on the Doubleword
 * route sends the fast id, attribution prices it as itself, and the served
 * tier is read from whichever id the response echoes.
 */
const DOUBLEWORD_FAST_DEPLOYMENT_MODEL_IDS: Readonly<Record<string, DoublewordProviderModelId>> = {
  [PROVIDER_MODEL_IDS.DOUBLEWORD.DEEPSEEK_V4_FLASH_0731]:
    PROVIDER_MODEL_IDS.DOUBLEWORD.DEEPSEEK_V4_FLASH_0731_FAST,
};

/** The fast deployment id a standard Doubleword deployment id maps to, if it has one. */
export function getDoublewordFastDeploymentModelId(
  modelId: string,
): DoublewordProviderModelId | undefined {
  return Object.hasOwn(DOUBLEWORD_FAST_DEPLOYMENT_MODEL_IDS, modelId)
    ? DOUBLEWORD_FAST_DEPLOYMENT_MODEL_IDS[modelId]
    : undefined;
}

/**
 * The tier label telemetry uses for a Doubleword fast-deployment attempt, both
 * as `requestedServiceTier` and as `servedServiceTier` when the response
 * echoes the fast id. One constant because the readout compares the two by
 * equality.
 */
export const DOUBLEWORD_FAST_DEPLOYMENT_SERVED_TIER = 'fast-deployment';

/** The served tier a Doubleword deployment id stands for, or `undefined` for an unknown id. */
export function getDoublewordDeploymentServedTier(
  modelId: string,
): typeof DOUBLEWORD_FAST_DEPLOYMENT_SERVED_TIER | 'default' | undefined {
  const fastDeploymentIds: readonly string[] = Object.values(DOUBLEWORD_FAST_DEPLOYMENT_MODEL_IDS);
  if (fastDeploymentIds.includes(modelId)) {
    return DOUBLEWORD_FAST_DEPLOYMENT_SERVED_TIER;
  }
  // The map is keyed by the standard deployment id, so a key hit is the standard deployment.
  return getDoublewordFastDeploymentModelId(modelId) ? 'default' : undefined;
}

/**
 * Checks if a model belongs to the DeepSeek V4 family.
 */
export function isDeepSeekV4Model(
  model: string,
): model is (typeof MODELS.DEEPSEEK)[keyof typeof MODELS.DEEPSEEK] {
  return model === MODELS.DEEPSEEK.DEEPSEEK_V4_FLASH || model === MODELS.DEEPSEEK.DEEPSEEK_V4_PRO;
}

/**
 * Checks if a model belongs to the GLM family.
 */
export function isGlmModel(model: string): model is (typeof MODELS.ZAI)[keyof typeof MODELS.ZAI] {
  return (
    model === MODELS.ZAI.GLM_5P1 ||
    model === MODELS.ZAI.GLM_5P2 ||
    model === MODELS.ZAI.GLM_5P3_FLASH
  );
}

function isNativeDeepSeekV4Effort(
  model: (typeof MODELS.DEEPSEEK)[keyof typeof MODELS.DEEPSEEK],
  effort: ReasoningEffort,
): boolean {
  return (
    effort === 'high' ||
    effort === 'xhigh' ||
    (model === MODELS.DEEPSEEK.DEEPSEEK_V4_FLASH && effort === 'low')
  );
}

/**
 * Map reasoning effort for DeepSeek V4 models, whose provider-native maximum is
 * represented by our existing `xhigh` tier rather than the literal `max` effort.
 * Flash supports native low effort. Other lower tiers are upgraded to high
 * for models that require the legacy reasoning levels.
 */
export function mapReasoningEffortForDeepSeekV4(
  model: (typeof MODELS.DEEPSEEK)[keyof typeof MODELS.DEEPSEEK],
  effort?: ReasoningEffort,
): ReasoningEffort | undefined {
  if (effort && isNativeDeepSeekV4Effort(model, effort)) {
    return effort;
  }

  if (effort === 'minimal' || effort === 'low' || effort === 'medium') {
    return 'high';
  }

  return effort;
}

/**
 * Returns the Google thinking config mode for a model, if defined.
 * This centralizes Gemini model-specific thinking behavior.
 */
export function getGoogleThinkingConfigType(model: string): GoogleThinkingConfigType | undefined {
  if (!isValidModelName(model)) {
    return undefined;
  }
  return getCatalogEntry(model).googleThinkingConfigType;
}

/**
 * Checks if a model belongs to the Google Vertex/Gemini family.
 * Use this instead of string prefix checks for robustness.
 */
export function isGeminiModel(model: string): boolean {
  if (!isValidModelName(model)) {
    return false;
  }
  return MODEL_CATALOG[model].family === 'google_vertex';
}

/**
 * Checks if a model belongs to the OpenAI family.
 *
 * These dispatch through the Responses API rather than Chat Completions —
 * whether on Azure, Bedrock, or the Vercel Gateway.
 */
export function isOpenAiFamilyModel(model: string): boolean {
  if (!isValidModelName(model)) {
    return false;
  }
  return MODEL_CATALOG[model].family === 'openai';
}

// ============================================================================
// getModelConfigsFor - Expand one model into its providers
// ============================================================================

/**
 * Returns the configured providers for this model only, in catalog order.
 *
 * Callers own their complete model sequence and reasoning settings. This helper
 * never appends another model; include shared fallback sequences explicitly.
 * Provider weights and health checks are applied later by the fallback engine.
 *
 * @param modelName - The model to get configs for
 * @param reasoningEffort - Optional reasoning effort level (validated at runtime)
 * @returns Provider configs for the requested model, in catalog order
 */
export function getModelConfigsFor<M extends ModelName>(
  modelName: M,
  reasoningEffort?: ReasoningEffortForModel<M>,
): ModelConfig[] {
  const normalizedReasoningEffort = isDeepSeekV4Model(modelName)
    ? mapReasoningEffortForDeepSeekV4(modelName, reasoningEffort as ReasoningEffort | undefined)
    : reasoningEffort;

  // Runtime validation for model-specific extended reasoning efforts.
  // Cast needed: ReasoningEffortForModel<M> is a conditional type that TS can't resolve at call site
  assertReasoningEffortAllowed(modelName, normalizedReasoningEffort as ReasoningEffort | undefined);

  const entry = MODEL_CATALOG[modelName];
  const configs: ModelConfig[] = [];

  // Type assertion needed: catalog entries have literal tuple types, this normalizes them
  const providerChain = entry.providerChain as readonly ProviderChainStep[];

  for (let i = 0; i < providerChain.length; i++) {
    const step = providerChain[i]!;
    const providerLabel = PROVIDER_LABELS[step.provider];
    const logName =
      i === 0 ? `primary model (${providerLabel})` : `fallback #${i} (${providerLabel})`;

    // Build config based on whether the effective model supports reasoning
    // Use type guard to narrow modelName to ReasoningModelName
    const verbosity: VerbosityLevel = 'low';
    if (isReasoningModelName(modelName)) {
      configs.push({
        provider: step.provider,
        name: modelName,
        logName,
        ...(normalizedReasoningEffort
          ? { reasoningEffort: normalizedReasoningEffort as ReasoningEffort }
          : {}),
        ...(entry.supportsVerbosity ? { verbosity } : {}),
      });
    } else {
      configs.push({
        provider: step.provider,
        name: modelName,
        logName,
        ...(entry.supportsVerbosity ? { verbosity } : {}),
      });
    }
  }

  return configs;
}

/** One caller-selected model and its reasoning setting. */
export type ModelChoice = {
  modelName: ModelName;
  reasoningEffort?: ReasoningEffort;
};

/**
 * Expands an explicitly chosen model sequence into its configured providers.
 * Preserves order and per-model settings. Adds no models and removes no duplicates:
 * the caller owns the complete sequence, including any shared fallback lists.
 */
export function getModelConfigsForChain(choices: readonly ModelChoice[]): ModelConfig[] {
  return choices.flatMap(({ modelName, reasoningEffort }) =>
    getModelConfigsFor(modelName, reasoningEffort),
  );
}

/**
 * Promotes high-reasoning GPT-5.6 Luna ahead of the former GPT-5.4 Mini route
 * while preserving the exact Mini provider chain and effort as fallback.
 */
export function getGpt56LunaModelConfigsWithMiniFallback(
  miniReasoningEffort?: ReasoningEffortForModel<typeof MODELS.OPENAI.GPT_5_4_MINI>,
): ModelConfig[] {
  return getModelConfigsForChain([
    { modelName: MODELS.OPENAI.GPT_5_6_LUNA, reasoningEffort: 'high' },
    { modelName: MODELS.OPENAI.GPT_5_4_MINI, reasoningEffort: miniReasoningEffort },
  ]);
}

/**
 * Promotes high-reasoning GPT-6 Luna ahead of high-reasoning GPT-5.6 Luna, keeping
 * the exact GPT-5.4 Mini provider chain and effort as the final fallback.
 */
export function getGpt6LunaModelConfigsWithMiniFallback(
  miniReasoningEffort?: ReasoningEffortForModel<typeof MODELS.OPENAI.GPT_5_4_MINI>,
): ModelConfig[] {
  return getModelConfigsForChain([
    { modelName: MODELS.OPENAI.GPT_6_LUNA, reasoningEffort: 'high' },
    { modelName: MODELS.OPENAI.GPT_5_6_LUNA, reasoningEffort: 'high' },
    { modelName: MODELS.OPENAI.GPT_5_4_MINI, reasoningEffort: miniReasoningEffort },
  ]);
}

/**
 * Promotes medium-reasoning GPT-5.6 Luna ahead of the former GPT-5.4 Nano route
 * while preserving Nano's Azure and Gateway provider chain as fallback.
 */
export function getGpt56LunaModelConfigsWithNanoFallback(): ModelConfig[] {
  return getModelConfigsForChain([
    { modelName: MODELS.OPENAI.GPT_5_6_LUNA, reasoningEffort: 'medium' },
    { modelName: MODELS.OPENAI.GPT_5_4_NANO },
  ]);
}

/**
 * Medium-reasoning GPT-6 Luna with GPT-5.4 Nano's provider chain as the
 * cross-model fallback, for the PR chat sidebar's latency-sensitive helpers.
 */
export function getGpt6LunaModelConfigsWithNanoFallback(): ModelConfig[] {
  return getModelConfigsForChain([
    { modelName: MODELS.OPENAI.GPT_6_LUNA, reasoningEffort: 'medium' },
    { modelName: MODELS.OPENAI.GPT_5_4_NANO },
  ]);
}

/**
 * Fallback-engine params for utility callers migrated from direct
 * single-model AI SDK calls. Keeps the caller on exactly one model — the
 * chain expands only into that model's own catalog provider rungs, never a
 * different model — matching their pre-engine behavior.
 */
export function getSingleModelFallbackParams<M extends ModelName>(
  model: M,
): { modelConfigs: ModelConfig[] } {
  return {
    modelConfigs: getModelConfigsFor(model),
  };
}

// ============================================================================
// WIKI & SEARCH CONFIG
// ============================================================================

/**
 *  Wiki Generation Configuration
 */

// Default wiki generation settings
export const WIKI_CONFIG = {
  DEFAULT_MODEL: MODELS.DEEPSEEK.DEEPSEEK_V4_FLASH,
  DEFAULT_LANGUAGE: 'en',
  DEFAULT_COMPREHENSIVE: true,
} as const;

// Default search model (separate from wiki generation)
export const SEARCH_CONFIG = {
  // Default model for search
  DEFAULT_MODEL: MODELS.OPENAI.GPT_6_LUNA,

  // Default language for search
  DEFAULT_LANGUAGE: 'en',
} as const;

export function getWikiGenerationParams(
  overrides: {
    model?: string;
    language?: string;
    comprehensive?: boolean;
  } = {},
) {
  return {
    model: overrides.model ?? WIKI_CONFIG.DEFAULT_MODEL,
    language: overrides.language ?? WIKI_CONFIG.DEFAULT_LANGUAGE,
    comprehensive: overrides.comprehensive ?? WIKI_CONFIG.DEFAULT_COMPREHENSIVE,
  };
}

/**
 * Helper function to get the default search parameters
 * Can be overridden by passing specific values
 */
export function getSearchParams(
  overrides: {
    model?: string;
    language?: string;
  } = {},
) {
  return {
    model: overrides.model ?? SEARCH_CONFIG.DEFAULT_MODEL,
    language: overrides.language ?? SEARCH_CONFIG.DEFAULT_LANGUAGE,
  };
}
